import React, { useRef, useState } from 'react';

export const ADMISSION_SECTION_ID = 'admission';

/**
 * The admissions call to action.
 *
 * The form itself is not built yet, so the button discloses a short panel in
 * place rather than opening a modal. When the real workflow ships, pass an
 * `onApply` handler from SchoolProfilePage — the button hands off to it, the
 * panel and its notice disappear, and the layout is unchanged.
 */
const SchoolAdmissionSection = ({ profile, onApply = null }) => {
  const { schoolName, phone } = profile;
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const panelRef = useRef(null);

  const handleApplyClick = () => {
    if (onApply) {
      onApply(profile);
      return;
    }
    setIsPanelOpen((open) => !open);
  };

  return (
    <section
      className="sp-section"
      id={ADMISSION_SECTION_ID}
      aria-labelledby="sp-admission-heading"
    >
      <div className="sp-shell">
        <div className="sp-admission-panel">
          <div className="sp-admission-copy">
            <p className="sp-label">Admissions</p>
            <h2 className="sp-display sp-display-m sp-admission-title" id="sp-admission-heading">
              Ready to join {schoolName}?
            </h2>
            <p className="sp-standfirst sp-admission-lead">
              Start your child&apos;s application and our admissions team will guide you
              through every step, from enrolment requirements to your first campus visit.
            </p>
          </div>

          <div className="sp-admission-action">
            <div className="sp-actions">
              <button
                type="button"
                className="sp-btn sp-btn-primary"
                onClick={handleApplyClick}
                aria-expanded={onApply ? undefined : isPanelOpen}
                aria-controls={onApply ? undefined : 'sp-admission-panel'}
              >
                Apply for Admission
              </button>

              {phone && (
                <a className="sp-link-action" href={phone.href}>
                  Speak to admissions
                </a>
              )}
            </div>

            {!onApply && (
              <div
                className="sp-admission-disclosure"
                id="sp-admission-panel"
                ref={panelRef}
                hidden={!isPanelOpen}
              >
                <p>
                  {schoolName} is preparing to accept applications online through
                  AtadaBoard. Applications open shortly
                  {phone
                    ? ' — until then, call the school and the admissions team will get you started.'
                    : ' — until then, please use the contact details above.'}
                </p>
                {phone && (
                  <a className="sp-btn sp-btn-outline" href={phone.href}>
                    Call {phone.display}
                  </a>
                )}
              </div>
            )}

            {!onApply && !isPanelOpen && (
              <p className="sp-admission-note">Online applications open soon.</p>
            )}
          </div>
        </div>
      </div>

      <style>{`
        /*
         * A tinted panel rather than a full-bleed colour band: the page keeps
         * one ground, and the accent stays a mark instead of a slab.
         */
        .sp-admission-panel {
          display: grid;
          grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
          gap: clamp(1.75rem, 4vw, 4rem);
          align-items: start;
          padding: clamp(1.75rem, 4vw, 3rem);
          background: var(--sp-accent-soft);
          border: 1px solid var(--sp-accent-border);
          border-left: 3px solid var(--sp-accent);
          border-radius: 3px;
        }

        .sp-admission-title {
          margin-top: 0.85rem;
        }

        .sp-admission-lead {
          margin-top: 1rem;
          max-width: 46ch;
        }

        .sp-admission-action {
          padding-top: 0.5rem;
        }

        .sp-admission-note {
          margin: 1rem 0 0 0;
          font-size: 0.9rem;
          color: var(--sp-ink-muted);
        }

        .sp-admission-disclosure {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--sp-accent-border);
        }

        .sp-admission-disclosure p {
          margin: 0 0 1.25rem 0;
          font-size: 0.975rem;
          line-height: 1.65;
          color: var(--sp-ink-soft);
          text-wrap: pretty;
        }

        @media (max-width: 860px) {
          .sp-admission-panel {
            grid-template-columns: 1fr;
          }

          .sp-admission-action {
            padding-top: 0;
          }
        }
      `}</style>
    </section>
  );
};

export default SchoolAdmissionSection;
